/**
 * Hook to fetch and manage chat session list from the REST API.
 */

import { useState, useEffect, useCallback } from 'react'
import { ChatSessionSummary } from '../types/chat-history'
import { supabase } from '../lib/supabase'
import { fetchWithTimeout } from '../lib/http'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

interface UseChatSessionsReturn {
  sessions: ChatSessionSummary[]
  loading: boolean
  error: string | null
  refresh: () => void
}

export function useChatSessions(): UseChatSessionsReturn {
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Build headers - include auth token if available
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      try {
        if (supabase) {
          const { data: { session } } = await supabase.auth.getSession()
          if (session?.access_token) {
            headers['Authorization'] = `Bearer ${session.access_token}`
          }
        }
      } catch {
        // Auth unavailable — proceed without token (dev mode)
      }
      // In dev mode, backend accepts requests without auth token

      const response = await fetchWithTimeout(`${API_BASE_URL}/api/chat/sessions`, {
        headers,
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.status}`)
      }

      const data: ChatSessionSummary[] = await response.json()
      setSessions(data)
    } catch (err) {
      console.error('[useChatSessions] Error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  return { sessions, loading, error, refresh: fetchSessions }
}
